import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Shield, LogOut, RefreshCw, Mail, MapPin, Activity, BarChart3 } from "lucide-react";
import { API } from "../lib/api";
import { toast } from "sonner";

const TOKEN_KEY = "netfotech_admin_token";

async function apiFetch(path, token) {
    const res = await fetch(`${API}${path}`, {
        headers: { "X-Admin-Token": token },
    });
    if (res.status === 401) throw new Error("Unauthorized");
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

export default function Admin() {
    const [token, setToken] = useState(() => {
        try {
            return sessionStorage.getItem(TOKEN_KEY) || "";
        } catch {
            return "";
        }
    });
    const [inputToken, setInputToken] = useState("");
    const [summary, setSummary] = useState(null);
    const [submissions, setSubmissions] = useState(null);
    const [loading, setLoading] = useState(false);

    const loadAll = async (t) => {
        if (!t) return;
        setLoading(true);
        try {
            const [sum, subs] = await Promise.all([
                apiFetch("/analytics/summary", t),
                apiFetch("/contact/submissions?limit=20", t),
            ]);
            setSummary(sum);
            setSubmissions(subs);
        } catch (e) {
            if (String(e.message).includes("Unauthorized")) {
                toast.error("Invalid admin token.");
                sessionStorage.removeItem(TOKEN_KEY);
                setToken("");
            } else {
                toast.error("Couldn't load dashboard.", { description: String(e.message) });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) loadAll(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onLogin = async (e) => {
        e.preventDefault();
        const t = inputToken.trim();
        if (!t) return;
        setLoading(true);
        try {
            await apiFetch("/analytics/summary", t);
            sessionStorage.setItem(TOKEN_KEY, t);
            setToken(t);
            setInputToken("");
            toast.success("Authenticated.");
            await loadAll(t);
        } catch {
            toast.error("Invalid admin token.");
        } finally {
            setLoading(false);
        }
    };

    const onLogout = () => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setSummary(null);
        setSubmissions(null);
    };

    // --- Gate: login ---
    if (!token) {
        return (
            <main data-testid="page-admin" className="relative pt-32 md:pt-40 min-h-screen">
                <div className="absolute inset-0 -z-10 grid-lines opacity-40" />
                <div className="max-w-md mx-auto px-5 md:px-8 lg:px-10 pb-32">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-linear shadow-glow-brand mb-6">
                        <Shield className="h-6 w-6 text-white" />
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter">
                        Admin dashboard
                    </h1>
                    <p className="mt-3 text-zinc-400 text-[14.5px] leading-relaxed">
                        Paste the admin token to see analytics, contact submissions and career applications.
                    </p>
                    <form
                        onSubmit={onLogin}
                        data-testid="admin-login-form"
                        className="mt-8 glass rounded-3xl border border-white/5 p-6"
                    >
                        <label className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                            Admin token
                        </label>
                        <input
                            type="password"
                            autoComplete="off"
                            value={inputToken}
                            onChange={(e) => setInputToken(e.target.value)}
                            data-testid="admin-token-input"
                            className="mt-2 w-full rounded-xl border border-white/10 bg-ink-800/80 px-4 py-3 text-[14px] text-white placeholder:text-zinc-600 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none font-mono"
                            placeholder="Paste token here"
                        />
                        <button
                            type="submit"
                            disabled={loading || !inputToken}
                            data-testid="admin-login-submit"
                            className="btn-glow relative mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-linear px-6 py-3 text-[14px] font-medium text-white hover:shadow-glow-brand transition-all disabled:opacity-60"
                        >
                            {loading ? "Checking…" : "Authenticate"}
                        </button>
                    </form>
                </div>
            </main>
        );
    }

    // --- Dashboard ---
    const total = summary?.total_events ?? 0;
    const by = summary?.by_event || [];
    const recent = summary?.recent || [];
    const maxCount = Math.max(1, ...by.map((x) => x.count));

    return (
        <main data-testid="page-admin" className="relative pt-32 md:pt-40 pb-24 min-h-screen">
            <div className="absolute inset-0 -z-10 grid-lines opacity-30" />
            <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brand-green">
                            <Shield className="h-3.5 w-3.5" />
                            Admin
                        </div>
                        <h1 className="mt-3 font-display text-3xl md:text-5xl font-bold tracking-tighter">
                            Netfotech insights
                        </h1>
                        <p className="mt-2 text-zinc-400 text-[14.5px]">
                            {total} events logged · {submissions?.length || 0} contact submissions
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => loadAll(token)}
                            disabled={loading}
                            data-testid="admin-refresh"
                            className="inline-flex items-center gap-2 rounded-full glass border border-white/10 px-4 py-2 text-[12.5px] text-zinc-300 hover:border-white/30 transition"
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </button>
                        <button
                            type="button"
                            onClick={onLogout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-2 rounded-full glass border border-white/10 px-4 py-2 text-[12.5px] text-zinc-300 hover:border-white/30 transition"
                        >
                            <LogOut className="h-3.5 w-3.5" />
                            Logout
                        </button>
                    </div>
                </div>

                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Events by type */}
                    <section
                        data-testid="admin-events-by-type"
                        className="lg:col-span-7 glass rounded-3xl border border-white/5 p-6 md:p-8"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <BarChart3 className="h-5 w-5 text-brand-green" />
                            <h2 className="font-display text-xl font-semibold">Events by type</h2>
                        </div>
                        {by.length === 0 ? (
                            <div className="text-zinc-500 text-sm py-10 text-center">
                                No events recorded yet.
                            </div>
                        ) : (
                            <ul className="space-y-3">
                                {by.map((row) => {
                                    const pct = Math.round((row.count / maxCount) * 100);
                                    return (
                                        <li
                                            key={row.event}
                                            className="flex items-center gap-4"
                                            data-testid={`admin-event-bar-${row.event}`}
                                        >
                                            <div className="w-56 shrink-0 text-[13px] text-zinc-300 truncate font-mono">
                                                {row.event}
                                            </div>
                                            <div className="flex-1 h-3 rounded-full bg-white/[0.03] overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${pct}%` }}
                                                    transition={{ duration: 0.6, ease: "easeOut" }}
                                                    className="h-full bg-brand-linear rounded-full"
                                                />
                                            </div>
                                            <div className="w-12 text-right font-mono text-[12px] text-zinc-400">
                                                {row.count}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>

                    {/* Recent events feed */}
                    <section
                        data-testid="admin-recent-events"
                        className="lg:col-span-5 glass rounded-3xl border border-white/5 p-6 md:p-8"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <Activity className="h-5 w-5 text-brand-blue" />
                            <h2 className="font-display text-xl font-semibold">Live event feed</h2>
                        </div>
                        {recent.length === 0 ? (
                            <div className="text-zinc-500 text-sm py-10 text-center">
                                Waiting for events…
                            </div>
                        ) : (
                            <ul className="space-y-2.5 max-h-[420px] overflow-auto pr-2">
                                {recent.map((e, i) => (
                                    <li
                                        key={`${e.session_id}-${i}`}
                                        className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-3 text-[12.5px]"
                                    >
                                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-green shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-mono text-white truncate">
                                                {e.event}
                                            </div>
                                            <div className="text-zinc-500 truncate">
                                                {e.path || ""}
                                            </div>
                                        </div>
                                        <div className="font-mono text-[10.5px] text-zinc-500 shrink-0">
                                            {formatDate(e.created_at)}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>

                {/* Contact submissions */}
                <section
                    data-testid="admin-contact-submissions"
                    className="mt-6 glass rounded-3xl border border-white/5 p-6 md:p-8"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <Mail className="h-5 w-5 text-brand-green" />
                        <h2 className="font-display text-xl font-semibold">
                            Latest contact submissions
                        </h2>
                    </div>
                    {!submissions || submissions.length === 0 ? (
                        <div className="text-zinc-500 text-sm py-10 text-center">
                            No contact submissions yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-[13px]">
                                <thead>
                                    <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-zinc-500 border-b border-white/5">
                                        <th className="py-3 pr-4 font-medium">When</th>
                                        <th className="py-3 pr-4 font-medium">Name</th>
                                        <th className="py-3 pr-4 font-medium">Email</th>
                                        <th className="py-3 pr-4 font-medium">Company</th>
                                        <th className="py-3 pr-4 font-medium">Industry</th>
                                        <th className="py-3 pr-4 font-medium">Subject</th>
                                        <th className="py-3 pr-4 font-medium">Email sent</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {submissions.map((s) => (
                                        <tr
                                            key={s.id}
                                            data-testid={`admin-submission-${s.id}`}
                                            className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                                        >
                                            <td className="py-3 pr-4 text-zinc-500 font-mono text-[11.5px] whitespace-nowrap">
                                                {formatDate(s.created_at)}
                                            </td>
                                            <td className="py-3 pr-4 text-white">{s.name}</td>
                                            <td className="py-3 pr-4 text-brand-blue">
                                                <a href={`mailto:${s.email}`}>{s.email}</a>
                                            </td>
                                            <td className="py-3 pr-4 text-zinc-300">{s.company || ""}</td>
                                            <td className="py-3 pr-4 text-zinc-400">{s.industry || ""}</td>
                                            <td className="py-3 pr-4 text-zinc-300 max-w-[260px] truncate">
                                                {s.subject || "General Inquiry"}
                                            </td>
                                            <td className="py-3 pr-4">
                                                {s.email_sent ? (
                                                    <span className="text-brand-green">Yes</span>
                                                ) : (
                                                    <span className="text-zinc-500">No</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </main>
    );
}
